import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Monitor, Smartphone, Globe, LogOut, ShieldCheck, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api';
import { AvatarUpload } from '../../components/AvatarUpload';
import { BannerUpload } from '../../components/BannerUpload';

function parseUserAgent(ua: string | null): { browser: string; os: string; isMobile: boolean } {
  if (!ua) return { browser: 'Unknown browser', os: 'Unknown OS', isMobile: false };

  let browser = 'Unknown browser';
  let os = 'Unknown OS';
  let isMobile = false;

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('OPR/') || ua.includes('Opera/')) browser = 'Opera';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/')) browser = 'Safari';

  if (ua.includes('Android')) { os = 'Android'; isMobile = true; }
  else if (ua.includes('iPhone') || ua.includes('iPad')) { os = 'iOS'; isMobile = true; }
  else if (ua.includes('Windows NT')) os = 'Windows';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';

  return { browser, os, isMobile };
}

type SettingsTab = 'profile' | 'security' | 'discord';

interface Session {
  id: string;
  deviceInfo: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

export default function Settings() {
  const { user, checkAuth } = useAuthStore();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  // Profile form state
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '');
  const [profileBannerUrl, setProfileBannerUrl] = useState(user?.profileBannerUrl || '');

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const updateProfileMutation = useMutation({
    mutationFn: (data: { displayName?: string; bio?: string; avatarUrl?: string; profileBannerUrl?: string }) =>
      api.put('/users/profile', data),
    onSuccess: () => {
      toast.success('Profile updated successfully!');
      checkAuth(); // Refresh user data
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.put('/users/password', data),
    onSuccess: () => {
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to change password');
    },
  });

  const { data: sessionsData, isLoading: sessionsLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<{ data: { sessions: Session[] } }>('/users/sessions'),
    enabled: activeTab === 'security',
  });

  const sessions: Session[] = (sessionsData as any)?.data?.sessions ?? [];

  const revokeSessionMutation = useMutation({
    mutationFn: (sessionId: string) => api.delete(`/users/sessions/${sessionId}`),
    onSuccess: () => {
      toast.success('Session revoked');
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to revoke session');
    },
  });

  const revokeAllSessionsMutation = useMutation({
    mutationFn: () => api.delete('/users/sessions'),
    onSuccess: () => {
      toast.success('All other sessions signed out');
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to sign out other sessions');
    },
  });

  // Discord
  const { data: discordData, isLoading: discordLoading } = useQuery({
    queryKey: ['discord-status'],
    queryFn: () => api.get('/auth/discord/status'),
    enabled: activeTab === 'discord',
  });
  const discordStatus = (discordData as any)?.data;

  const linkDiscordMutation = useMutation({
    mutationFn: async () => {
      const res = await api.get('/auth/discord') as any;
      window.location.href = res.data.url;
    },
    onError: () => toast.error('Failed to start Discord authorization.'),
  });

  const unlinkDiscordMutation = useMutation({
    mutationFn: () => api.delete('/auth/discord'),
    onSuccess: () => {
      toast.success('Discord unlinked.');
      queryClient.invalidateQueries({ queryKey: ['discord-status'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to unlink Discord.');
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate({
      displayName: displayName || undefined,
      bio: bio || undefined,
      avatarUrl: avatarUrl || undefined,
      profileBannerUrl: profileBannerUrl || undefined,
    });
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    changePasswordMutation.mutate({ currentPassword, newPassword });
  };

  const tabs: { id: SettingsTab; label: string }[] = [
    { id: 'profile', label: 'Profile' },
    { id: 'security', label: 'Security' },
    { id: 'discord', label: 'Discord' },
  ];

  return (
    <div>
      <h1 className="font-western text-3xl text-cream mb-8">Settings</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-8 border-b border-gold/20">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 font-medium transition-colors relative ${
              activeTab === tab.id
                ? 'text-gold'
                : 'text-text-secondary hover:text-cream'
            }`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold" />
            )}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="max-w-2xl">
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            {/* Avatar Upload */}
            <div className="flex flex-col items-center sm:flex-row sm:items-start gap-6">
              <AvatarUpload
                currentAvatarUrl={avatarUrl || user?.avatarUrl}
                onUploadSuccess={(url) => {
                  setAvatarUrl(url);
                  // Auto-save the avatar URL
                  updateProfileMutation.mutate({ avatarUrl: url });
                }}
              />
              <div className="flex-1 text-center sm:text-left">
                <p className="text-text-secondary text-sm">
                  Upload a profile picture to personalize your account.
                </p>
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-text-muted text-sm mb-2">Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={user?.username}
                maxLength={100}
                className="input w-full"
              />
              <p className="text-text-muted text-xs mt-1">
                This is how your name will appear across the site
              </p>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-text-muted text-sm mb-2">Bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself..."
                maxLength={500}
                rows={4}
                className="input w-full resize-none"
              />
              <p className="text-text-muted text-xs mt-1">
                {bio.length}/500 characters
              </p>
            </div>

            {/* Profile Banner Upload */}
            <div>
              <label className="block text-text-muted text-sm mb-2">Profile Banner</label>
              <BannerUpload
                currentBannerUrl={profileBannerUrl || user?.profileBannerUrl}
                onUploadSuccess={(url) => {
                  setProfileBannerUrl(url);
                }}
              />
            </div>

            {/* Account Info (Read-only) */}
            <div className="border-t border-gold/10 pt-6">
              <h3 className="text-cream font-semibold mb-4">Account Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="block text-text-muted text-sm">Username</span>
                  <span className="text-cream">{user?.username}</span>
                </div>
                <div>
                  <span className="block text-text-muted text-sm">Email</span>
                  <span className="text-cream">{user?.email}</span>
                </div>
                <div>
                  <span className="block text-text-muted text-sm">Member Since</span>
                  <span className="text-cream">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="block text-text-muted text-sm">Role</span>
                  <span className="text-cream capitalize">{user?.role}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={updateProfileMutation.isPending}
                className="btn-primary"
              >
                {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="max-w-2xl">
          <div className="card p-6">
            <h2 className="text-cream font-semibold mb-6">Change Password</h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-text-muted text-sm mb-2">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-text-muted text-sm mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="input w-full"
                />
                <p className="text-text-muted text-xs mt-1">
                  Minimum 8 characters
                </p>
              </div>

              <div>
                <label className="block text-text-muted text-sm mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="input w-full"
                />
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={changePasswordMutation.isPending}
                  className="btn-primary"
                >
                  {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>

          <div className="card p-6 mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-cream font-semibold">Active Sessions</h2>
              {sessions.filter((s) => !s.isCurrent).length > 0 && (
                <button
                  onClick={() => revokeAllSessionsMutation.mutate()}
                  disabled={revokeAllSessionsMutation.isPending}
                  className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                >
                  <LogOut size={14} />
                  {revokeAllSessionsMutation.isPending ? 'Signing out...' : 'Sign out all other sessions'}
                </button>
              )}
            </div>

            {sessionsLoading ? (
              <div className="flex items-center justify-center py-8 text-text-secondary">
                <Loader2 size={20} className="animate-spin mr-2" />
                Loading sessions...
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-text-secondary text-sm">No active sessions found.</p>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => {
                  const { browser, os, isMobile } = parseUserAgent(session.userAgent);
                  const DeviceIcon = isMobile ? Smartphone : Monitor;
                  return (
                    <div
                      key={session.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        session.isCurrent
                          ? 'border-gold/30 bg-gold/5'
                          : 'border-white/5 bg-white/2'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 ${session.isCurrent ? 'text-gold' : 'text-text-secondary'}`}>
                          <DeviceIcon size={18} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-cream text-sm font-medium">
                              {browser} on {os}
                            </span>
                            {session.isCurrent && (
                              <span className="text-xs bg-gold/20 text-gold px-2 py-0.5 rounded-full font-medium">
                                This device
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            {session.ipAddress && (
                              <span className="flex items-center gap-1 text-text-muted text-xs">
                                <Globe size={11} />
                                {session.ipAddress}
                              </span>
                            )}
                            <span className="text-text-muted text-xs">
                              Signed in {new Date(session.createdAt).toLocaleDateString(undefined, {
                                month: 'short', day: 'numeric', year: 'numeric',
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                      {!session.isCurrent && (
                        <button
                          onClick={() => revokeSessionMutation.mutate(session.id)}
                          disabled={revokeSessionMutation.isPending}
                          className="text-xs text-red-400 hover:text-red-300 border border-red-400/30 hover:border-red-300/50 px-3 py-1.5 rounded transition-colors disabled:opacity-50 shrink-0 ml-4"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center gap-2 text-success text-sm mt-6 pt-4 border-t border-gold/10">
              <ShieldCheck size={15} />
              <span>Your account is secured with password authentication</span>
            </div>
          </div>
        </div>
      )}

      {/* Discord Tab */}
      {activeTab === 'discord' && (
        <div className="card p-6 max-w-xl">
          <h2 className="text-cream font-semibold text-lg mb-1">Discord Integration</h2>
          <p className="text-text-secondary text-sm mb-6">
            Link your Discord account to redeem purchased keys using the <code className="bg-leather px-1 rounded">/redeem</code> command in our bot.
          </p>

          {discordLoading ? (
            <div className="flex items-center gap-2 text-text-secondary text-sm">
              <Loader2 className="animate-spin w-4 h-4" /> Loading...
            </div>
          ) : discordStatus?.linked ? (
            <div className="space-y-4">
              {/* Linked state */}
              <div className="flex items-center gap-4 bg-background rounded-lg p-4">
                {discordStatus.discordAvatar && (
                  <img
                    src={discordStatus.discordAvatar}
                    alt="Discord avatar"
                    className="w-14 h-14 rounded-full border-2 border-gold/40"
                  />
                )}
                <div>
                  <p className="text-cream font-semibold">{discordStatus.discordUsername}</p>
                  <p className="text-text-muted text-xs">ID: {discordStatus.discordId}</p>
                  {discordStatus.discordLinkedAt && (
                    <p className="text-text-muted text-xs">
                      Linked on {new Date(discordStatus.discordLinkedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 text-sm text-green-400">
                Discord is linked. Use <code className="bg-black/20 px-1 rounded">/redeem &lt;token&gt;</code> in the Outlaw Oasis Discord to reveal your keys.
              </div>

              <a
                href="https://discord.gg/u8Tuv7ns5r"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary block text-center"
              >
                Open Outlaw Oasis Discord
              </a>

              <button
                onClick={() => {
                  if (confirm('Are you sure you want to unlink your Discord account?')) {
                    unlinkDiscordMutation.mutate();
                  }
                }}
                disabled={unlinkDiscordMutation.isPending}
                className="w-full py-2 px-4 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors text-sm disabled:opacity-50"
              >
                {unlinkDiscordMutation.isPending ? 'Unlinking...' : 'Unlink Discord'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-gold/10 border border-gold/20 rounded-lg p-4 text-sm text-text-secondary">
                Once linked, your Discord account will be permanently tied to this Outlaw Oasis account.
                Only you will be able to redeem your keys on Discord.
              </div>

              <button
                onClick={() => linkDiscordMutation.mutate()}
                disabled={linkDiscordMutation.isPending}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {linkDiscordMutation.isPending ? (
                  <><Loader2 className="animate-spin w-4 h-4" /> Redirecting...</>
                ) : (
                  'Link Discord Account'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
